import React from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { BuyerSelect } from "components/form_components/BuyerSelect";
import Button from "components/ui/button/Button";
import Drawer from "components/ui/drawer/Drawer";
import toast from "components/ui/toast/Toast";
import { useAddBuyerEntries } from "lib/hooks/api/buyer_lists/useAddBuyerEntries";
import { EventNames, useTracking } from "lib/tracking";
import { pluralise } from "lib/utils";

export type AddBuyersToListDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  list: { id: string; name: string; entries: { id: string }[] };
};

type FormValues = { buyerIds: string[] };

/**
 * Slideout drawer for searching for buyers and adding them to a buyer list.
 */
export function AddBuyersToListDrawer({ isOpen, onClose, list }: AddBuyersToListDrawerProps) {
  const { logEvent } = useTracking();
  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: { buyerIds: [] },
  });
  const selectedCount = watch("buyerIds").length;

  const { isLoading, mutate } = useAddBuyerEntries({
    onSuccess: (_, variables) => {
      logEvent(variables.buyerIds.length > 1 ? EventNames.buyerBulkSave : EventNames.buyerSaved, {
        buyerGuids: variables.buyerIds,
        buyerListName: list.name,
        buyerListGuid: list.id,
        "Context source": "Buyer list page",
      });
      toast.success(`${pluralise(variables.buyerIds.length, "buyer")} added to ${list.name}`);
      onClose();
    },
    onError: () => toast.error(`Failed to add buyers to ${list.name}`),
  });

  const onSubmit = ({ buyerIds }: FormValues) => {
    const existingIds = new Set(list.entries.map((entry) => entry.id));
    const newIds = buyerIds.filter((buyerId) => !existingIds.has(buyerId));
    if (newIds.length === 0) {
      toast.message(`Selected buyers are already in ${list.name}`);
      onClose();
      return;
    }
    mutate({ id: list.id, buyerIds: newIds });
  };

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()} width={486} padding={24}>
      <DrawerForm aria-label="Add buyers form" onSubmit={handleSubmit(onSubmit)}>
        <Flex justifyContent="space-between" alignItems="flex-start" gap={16}>
          <Text h3>Add buyers to {list.name}</Text>
          <Button
            as="button"
            buttonType="secondary"
            icon="xClose"
            onClick={onClose}
            aria-label="Close"
          />
        </Flex>
        <BuyerSelect
          name="buyerIds"
          label="Buyers"
          control={control}
          mode="multiple"
          placeholder="Search for buyers"
          listHeight={400}
        />
        <Footer justifyContent="flex-end" gap={8}>
          <Button buttonType="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading} disabled={selectedCount === 0}>
            {isLoading ? "Adding..." : "Add buyers"}
          </Button>
        </Footer>
      </DrawerForm>
    </Drawer>
  );
}

const DrawerForm = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: 24,
  height: "100%",
});

// Pinned to the bottom of the drawer so the open dropdown has room below the search field
const Footer = styled(Flex)({
  marginTop: "auto",
});
